import { useMemo } from 'react';

import flatMap from 'lodash/flatMap';

import { SECONDS_IN_DAY } from '@cast/constants';
import type { DateRange } from '@cast/shared-types';
import { NoDataReason } from '@cast/types';

import { usePaginatedDataTransferCostQuery } from 'hooks/queries/cost';
import { filterMostExpensiveItems } from 'features/cost-report/utils/filterMostExpensiveItems';
import {
  TopMostExpensiveGroup,
  TopMostExpensiveGroupDatapoint,
} from 'types/top-items';

import { mapWorkloadDataTransfer } from '../providers/_utils';
import { createChartData } from '../providers/WorkloadsNetworkCostOverTimeProvider/_hooks/useTopMostExpensiveWorkloads/_utils';
import { useNetworkCostPrerequisites } from './useNetworkCostPrerequisites';

export type UseNetworkCostOverTimeQueryArgs = {
  adjustedDateRange: DateRange;
  currentDateRange: DateRange;
};

export type UseNetworkCostOverTimeQueryResult = {
  topItems: TopMostExpensiveGroup[];
  datapoints: TopMostExpensiveGroupDatapoint;
  isLoading: boolean;
  isFetching: boolean;
  isCapabilitiesLoading: boolean;
  hasNetworkAgent: boolean;
  isDataProcessing: boolean;
  noDataReason?: NoDataReason;
  error?: unknown;
  refetch: () => void;
};

export const useNetworkCostOverTimeQuery = (
  useNetworkCostOverTimeQueryArgs: UseNetworkCostOverTimeQueryArgs
): UseNetworkCostOverTimeQueryResult => {
  const { adjustedDateRange, currentDateRange } =
    useNetworkCostOverTimeQueryArgs;
  const startTime = adjustedDateRange[0].toISOString();
  const endTime = adjustedDateRange[1].toISOString();

  const {
    clusterId,
    hasNetworkAgent,
    isCapabilitiesLoading,
    isDataProcessing,
    capabilitiesError,
    refetchCapabilities,
  } = useNetworkCostPrerequisites({ startTime, endTime });

  const { isLoading, isFetching, data, error, noDataReason, refetch } =
    usePaginatedDataTransferCostQuery({
      clusterId,
      startTime,
      endTime,
      stepSeconds: SECONDS_IN_DAY,
      pageLimit: 5,
      sortBy: 'totalCost',
      sortDirection: 'desc',
      enabled: !!clusterId && hasNetworkAgent,
    });

  const workloadDataTransfer = useMemo(() => {
    return mapWorkloadDataTransfer(
      flatMap(data.map((item) => item.items || []) || [])
    );
  }, [data]);

  const topItems = useMemo<TopMostExpensiveGroup[]>(() => {
    return filterMostExpensiveItems(workloadDataTransfer, (d) => d.cost.total, {
      mapFn: (d) => ({
        id: d.workloadName + d.namespace + d.workloadType,
        name: d.workloadName,
      }),
    });
  }, [workloadDataTransfer]);

  const filler = useMemo(
    () =>
      topItems.reduce<Record<string, null>>(
        (acc, { id }) => ({ ...acc, [id]: null }),
        {}
      ),
    [topItems]
  );

  const datapoints = useMemo<TopMostExpensiveGroupDatapoint>(() => {
    return createChartData(currentDateRange, workloadDataTransfer, filler);
  }, [currentDateRange, workloadDataTransfer, filler]);

  return {
    topItems,
    datapoints,
    isLoading: isLoading || isCapabilitiesLoading,
    isFetching,
    isCapabilitiesLoading,
    hasNetworkAgent,
    isDataProcessing,
    noDataReason,
    error: error || capabilitiesError,
    refetch: () => {
      refetch();
      refetchCapabilities();
    },
  };
};
